import { ClassNames, css } from '@emotion/react';
import { type KeyboardEvent, useEffect, useRef } from 'react';
import { FormProvider } from 'react-hook-form';
import t from 'react-translate';
import { config } from '@config/pendo.config.json';
import type { RowProps } from 'shared/components/nv-table';
import NvDropdown, { NvDropdownButtonStyle } from 'shared/components/inputs/nv-dropdown';
import NvSwitch from 'shared/components/inputs/nv-switch';
import AthenaTextInput from 'athena/components/text-input';
import { primary } from 'styles/global_defaults/colors';
import { textLargeBodyFontSize, textLargeLineHeight } from 'styles/global_defaults/fonts';
import { quarterSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import { ActionItemRowHandlers, PreAssignedActionItem } from '../types';
import { assigneeCellStyles } from './styles';
import useActionItemForm from './use-action-item-form';

type ActionItemEditRowProps = Pick<RowProps<PreAssignedActionItem>, 'className' | 'getCellStyle'> & {
  row: PreAssignedActionItem;
  isNewRow: boolean;
  setEditingId: ActionItemRowHandlers['setEditingId'];
  setShowAddRow: ActionItemRowHandlers['setShowAddRow'];
  onSave: ActionItemRowHandlers['onSave'];
  onHasUnsavedChanges: ActionItemRowHandlers['onHasUnsavedChanges'];
};

const rowBaseStyles = (isNewRow: boolean) => css`
  min-height: 65px;
  ${!isNewRow && css`
    margin-bottom: ${standardSpacing}px;
  `};
`;

const descriptionCellStyles = css`
  gap: ${quarterSpacing}px;

  > div { flex: 1; min-width: 0; }

  /* doubled .athena-input class bumps specificity to 4 vs AthenaTextInput's 3 */
  .athena-input-container .athena-input.athena-input {
    border: none;
    border-bottom: 1px solid ${primary};
    border-radius: 0;
    padding: 0 !important;
    height: ${textLargeLineHeight}px;
    font-size: ${textLargeBodyFontSize}px !important;
    line-height: ${textLargeLineHeight}px !important;

    &:focus {
      outline: none !important;
      box-shadow: none !important;
      border-bottom-width: 1px !important;
    }
  }
`;

const ActionItemEditRow = ({
  row, className, getCellStyle, isNewRow, setEditingId, setShowAddRow, onSave, onHasUnsavedChanges,
}: ActionItemEditRowProps) => {
  const translationBase = t.MENTORING_PROGRAMS.GUIDANCE.ACTION_ITEMS;
  const dataQATag = config.pendo.athena.mentorshipProgram.preAssignedActionItems;
  const {
    methods, formRequired, hasUnsavedChanges,
    editAssigneeOptions, editAssigneeTitle, editSelectedAssigneeIndex,
    handleKeyDown, handleSave, commitIfChanged, handleRequiredChange,
  } = useActionItemForm({ row, isNewRow, setShowAddRow, setEditingId, onSave });

  const assigneeDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    onHasUnsavedChanges(hasUnsavedChanges);
  }, [hasUnsavedChanges, onHasUnsavedChanges]);

  const handleDescriptionKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isNewRow) {
        assigneeDropdownRef.current?.focus();
      } else {
        commitIfChanged();
      }
      return;
    }
    handleKeyDown(e);
  };

  return (
    <FormProvider {...methods}>
      <ClassNames>
        {({ css: classNamesCss }) => (
          <form
            css={rowBaseStyles(isNewRow)}
            className={`${className} border-bottom border-gray-6 align-items-center`}
            onSubmit={methods.handleSubmit(handleSave)}
            data-qa={`${dataQATag.actionItemRow}_${row.id}`}
          >
            <div className={`${classNamesCss`${getCellStyle(0)}`} d-flex align-items-center`}>
              <div css={descriptionCellStyles} className='d-flex align-items-center w-100'>
                <AthenaTextInput
                  withForm
                  name='description'
                  placeholder={translationBase.PLACEHOLDER.DESCRIPTION()}
                  showLabel={false}
                  autoFocus
                  onKeyDown={handleDescriptionKeyDown}
                  onBlur={isNewRow ? undefined : commitIfChanged}
                  dataQa={dataQATag.descriptionInputField}
                />
              </div>
            </div>
            <div css={assigneeCellStyles} className={classNamesCss`${getCellStyle(1)}`}>
              <NvDropdown
                ref={assigneeDropdownRef}
                showRequiredIndicator
                items={editAssigneeOptions}
                title={editAssigneeTitle}
                buttonStyle={NvDropdownButtonStyle.FORM_SMALL}
                showSelectedIndicator
                initialIndex={editSelectedAssigneeIndex}
                toggleDataQa={dataQATag.assigneeDropdown.toggle}
              />
            </div>
            <div className={classNamesCss`${getCellStyle(2)}`}>
              <NvSwitch
                checked={formRequired}
                onChange={handleRequiredChange}
                ariaLabel={translationBase.ARIA_LABEL.REQUIRED_TOGGLE()}
                dataQa={dataQATag.requiredToggle}
              />
            </div>
            <div className={classNamesCss`${getCellStyle(3)}`} />
          </form>
        )}
      </ClassNames>
    </FormProvider>
  );
};

export default ActionItemEditRow;
